/** @format */

import { APP_BOOTSTRAP_LISTENER, APP_ID, ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import { CompareEffects, ProductListEffects, compareReducer, productListReducers } from '@plp/data-access/state';
import { CompareGridComponent, ComparePaintedDoorModalComponent, NotFoundComponent, ProductListSnackbarComponent } from '@plp/components';
import { CustomUrlSerializer, TaggingService, WindowModule } from '@shared/utils/services';
import { DCSG_APP_NAME, GlobalErrorHandler, LogDataAccessModule, LoggerConfig } from '@logger';
import { RouterModule, UrlSerializer } from '@angular/router';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { commonReducer, homrReducer } from '@state/data-access';
import { provideHttpClient, withFetch } from '@angular/common/http';

import { APP_BASE_HREF } from '@angular/common';
import { AUTH_URL_FILTER } from '@shared/authentication/data-access';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CeopsComponent } from '@shared/ceops';
import { EffectsModule } from '@ngrx/effects';
import { FamilyAppRoutingModule } from './app.routes';
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { ProductAnalyticsService } from '@shared/data-access/services';
import { ProductListCompareService } from '@plp/data-access/services';
import { ProductListContainerComponent } from './product-list/product-list-container/product-list-container.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { chain } from '@core/chain';
import { environment } from '@environment/plp';
import { headerLocationReducer } from '@pdp/data-access/state';

/*
 * App Configs
 */
const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
	prefix: ''
};

const LOGGER_CONFIG: LoggerConfig = {
	clientLoggerLevel: environment.logging.client.level,
	xhrLoggerLevel: environment.logging.xhr.level,
	xhrLoggerUrl: `${environment.api.spring.microServiceProxy.logger.path}publish`
};

/*
 * Module Imports
 */
const IMPORTS = [
	BrowserAnimationsModule,
	BrowserModule,
	CeopsComponent,
	ComparePaintedDoorModalComponent,
	CompareGridComponent,
	WindowModule.forRoot(LOCAL_STORAGE_CONFIG),
	LogDataAccessModule.forRoot(LOGGER_CONFIG),
	NotFoundComponent,
	ProductListContainerComponent,
	ProductListSnackbarComponent,
	RouterModule,
	StoreModule.forRoot({
		common: commonReducer,
		homr: homrReducer,
		headerLocation: headerLocationReducer,
		router: routerReducer
	}),
	EffectsModule.forRoot([ProductListEffects, CompareEffects]),
	StoreModule.forFeature('productList', {
		productListCore: productListReducers
	}),
	StoreModule.forFeature('compare', compareReducer),
	StoreRouterConnectingModule.forRoot(),
	StoreDevtoolsModule.instrument({
		maxAge: 25,
		logOnly: environment.production,
		connectInZone: true
	})
];

/*
 * Module Providers
 */
const PROVIDERS = [
	{provide: APP_ID, useValue:'dcsg-ngx-plp-server'},
	{ provide: APP_BASE_HREF, useValue: '/' },
	{
		provide: AUTH_URL_FILTER,
		useValue: [
			environment.baseUrls.pointService,
			environment.baseUrls.identityService,
			environment.baseUrls.athleteService,
			environment.api.myLocker
		]
	},
	/*
	 * APP_INITIALIZER InjectionToken is used to perform any logic during app initialization,
	 * this would execute before AppComponent or any Resolve Guards.  This guarantees anything
	 * required for setup is completed ahead of time.
	 */
	provideAppInitializer(() => {
		const initializerFn = ((service: ProductAnalyticsService) => () => service.resetAnalytics())(inject(ProductAnalyticsService));
		return initializerFn();
	}),
	{
		provide: APP_BOOTSTRAP_LISTENER,
		useFactory: (service: ProductListCompareService) => () => service.readyToInit.next(true),
		deps: [ProductListCompareService],
		multi: true
	},
	{ provide: Chain, useValue: chain || {} },
	{ provide: DCSG_APP_NAME, useValue: 'PLP' },
	{ provide: Environment, useValue: environment || {} },
	{ provide: ErrorHandler, useClass: GlobalErrorHandler },
	...HTTP_INTERCEPTOR_PROVIDERS,
	{ provide: UrlSerializer, useClass: CustomUrlSerializer },
	TaggingService,
	provideClientHydration(withEventReplay()),
	provideHttpClient(withFetch())
];

/**
 * NOTE: This is the main product app module and is for the standalone product app and does include the router
 */
@NgModule({
	declarations: [AppComponent],
	imports: [
		...IMPORTS,
		FamilyAppRoutingModule,
	],
	providers: PROVIDERS,
	exports: [AppComponent, RouterModule],
	bootstrap: [AppComponent]
})
export class FamilyAppModule {}
